import PropTypes from 'prop-types';
import { Controller, useFieldArray } from 'react-hook-form';
import { cn } from '#/lib/utils';

import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import {
  CUSTOMER_ADDRESS_TYPE_OPTIONS,
  addressTypesOverlap,
} from '#/modules/crmMgmt/customer/helper/customerAddress';
import { createEmptyAddressRow } from '#/modules/crmMgmt/customer/helper/buildCustomerWritePayload';

const CustomerAddressesEditor = ({
  control,
  register,
  errors,
  setValue,
  getValues,
}) => {
  const { fields, append } = useFieldArray({
    control,
    name: 'addresses',
    keyName: 'fieldKey',
  });

  function handleAddAddress() {
    append(createEmptyAddressRow());
  }

  function clearOverlappingDefaults(index, nextType) {
    fields.forEach((_, otherIndex) => {
      if (otherIndex === index) {
        return;
      }
      const otherType = getValues(`addresses.${otherIndex}.addressType`);
      if (!addressTypesOverlap(nextType, otherType)) {
        return;
      }
      setValue(`addresses.${otherIndex}.isDefault`, false, {
        shouldDirty: true,
      });
    });
  }

  function handleDefaultChange(index, checked) {
    setValue(`addresses.${index}.isDefault`, Boolean(checked), {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!checked) {
      return;
    }

    clearOverlappingDefaults(index, getValues(`addresses.${index}.addressType`));
  }

  function handleAddressTypeChange(index, nextType) {
    const isDefault = Boolean(getValues(`addresses.${index}.isDefault`));
    if (!isDefault) {
      return;
    }
    clearOverlappingDefaults(index, nextType);
  }

  return (
    <div className="form-section @container/form-section pb-4">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">地址</h2>
        <Button type="button" size="sm" onClick={handleAddAddress}>
          新增地址
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          尚未新增地址；儲存時將以空白資料儲存。
        </p>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          帳單、出貨用途各最多一個預設；「帳單＋出貨」同時佔兩個用途。
          地址為單一欄位字串。既有地址不可刪除（merge-only），僅可新增或修改。
        </p>
      )}

      {errors?.addresses?.message ? (
        <p className="mt-3 text-sm text-red-500">{errors.addresses.message}</p>
      ) : null}

      {fields.length > 0 ? (
        <div
          className={cn(
            'form-section__body',
            'grid grid-cols-1 gap-3',
            '@lg/form-section:grid-cols-2',
          )}
        >
          {fields.map((field, idx) => (
            <div
              key={field.fieldKey}
              className={cn(
                'px-3 py-5',
                'bg-gray-50',
                'border border-gray-200 rounded-md',
              )}
            >
              <FloatingSelect
                id={`addresses.${idx}.addressType`}
                label={`類型 #${idx + 1}`}
                options={CUSTOMER_ADDRESS_TYPE_OPTIONS}
                control={control}
                errors={errors}
                onChange={(nextType) => handleAddressTypeChange(idx, nextType)}
              />
              <FloatingInput
                id={`addresses.${idx}.postalCode`}
                label="郵遞區號"
                register={register}
                errors={errors}
              />
              <FloatingInput
                id={`addresses.${idx}.address`}
                label="地址"
                register={register}
                errors={errors}
              />
              <FloatingInput
                id={`addresses.${idx}.remark`}
                label="備註"
                register={register}
                errors={errors}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Controller
                  name={`addresses.${idx}.isDefault`}
                  control={control}
                  render={({ field: checkboxField }) => (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      預設地址
                      <Checkbox
                        checked={Boolean(checkboxField.value)}
                        onCheckedChange={(checked) => handleDefaultChange(idx, checked)}
                      />
                    </label>
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

CustomerAddressesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
};

export default CustomerAddressesEditor;
